import { ArrowForward } from "@mui/icons-material";
import { Box, Link } from "@mui/material";
import { useState } from "react";

import {
  CALIBRATE_FONT_FAMILY_ACCENT,
  CALIBRATE_SURFACE_HAIRLINE,
} from "components/hoc/Theme";
import { VIEWPORT_2K_MIN_MEDIA, VIEWPORT_4K_MIN_MEDIA } from "config";
import type { Partner } from "types/partners";
import type { RoleProfile } from "utils/roles";

import PartnerSpotlightCarousel from "./PartnerSpotlightCarousel";

/** The partner band is for client users; staff reach the directory from the menu. */
export function showsPartnerBand(profile: RoleProfile): boolean {
  return profile?.user_type === "client";
}

interface PartnerBandProps {
  /** The Partner Directory, inside the Client View shell when viewing. */
  directoryPath: string;
  /** The partners with a logo; undefined until the request settles. */
  partners: Partner[] | undefined;
}

/**
 * The white band of partner logos at the bottom of the home page. Its height is
 * fixed per screen tier, so fewer products above never make it taller.
 */
export default function PartnerBand({
  directoryPath,
  partners,
}: PartnerBandProps) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  return (
    <Box
      component="section"
      aria-label="Partners"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(event: React.FocusEvent<HTMLElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null))
          setFocused(false);
      }}
      sx={{
        flexShrink: 0,
        height: 120,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        px: { xs: 2, sm: 3 },
        pt: 1.5,
        pb: 1,
        overflow: "hidden",
        bgcolor: "background.paper",
        borderTop: `1px solid ${CALIBRATE_SURFACE_HAIRLINE}`,
        [VIEWPORT_2K_MIN_MEDIA]: { height: 164, px: 5, pt: 2, pb: 1.5 },
        [VIEWPORT_4K_MIN_MEDIA]: { height: 176, px: 6 },
      }}
    >
      <Link
        href={directoryPath}
        sx={{
          alignSelf: "flex-start",
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
          fontSize: 14,
          color: "text.secondary",
          textDecoration: "none",
          "&:hover": { textDecoration: "underline" },
          [VIEWPORT_2K_MIN_MEDIA]: { fontSize: 17 },
          [VIEWPORT_4K_MIN_MEDIA]: { fontSize: 18 },
        }}
      >
        Explore All Partners
        <ArrowForward aria-hidden sx={{ fontSize: "1.1em" }} />
      </Link>
      <Box
        sx={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center" }}
      >
        {/* Nothing until the directory answers: the built-in logos only stand
            in when it has none, instead of flashing before it does. */}
        {partners !== undefined && (
          <PartnerSpotlightCarousel
            partners={partners}
            paused={hovered || focused}
          />
        )}
      </Box>
    </Box>
  );
}
